import React, { useMemo } from "react";
import {
  Area,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  formatInrAmount as formatCurrency,
  formatIndianNumber as formatNumber,
  formatIndianPercent as formatPercent,
  formatCompactInrAxis as formatCompact,
  formatLitres,
} from "../../../utils/reportFormat";

const SPEND_COLORS = {
  fuel: "#2563EB",
  nonFuel: "#D97706",
  cylinder: "#7C3AED",
  total: "#5D38DB",
};

const PRICE_COLORS = {
  petrol: "#16A34A",
  diesel: "#2563EB",
  default: "#64748B",
};

const MIX_COLORS = ["#2563EB", "#D97706", "#7C3AED", "#64748B"];

const resolvePriceColor = (series) => {
  const name = String(series?.fuel || series?.label || "").toLowerCase();
  if (name.includes("diesel")) return PRICE_COLORS.diesel;
  if (name.includes("petrol")) return PRICE_COLORS.petrol;
  return PRICE_COLORS.default;
};

const SpendTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;

  const rows = [...payload]
    .filter((item) => item.value != null && item.value !== "")
    .sort((a, b) => (b.value || 0) - (a.value || 0));

  return (
    <div className="owner-chart-tooltip">
      <p className="owner-chart-tooltip__title">{label}</p>
      {rows.map((item) => (
        <div className="owner-chart-tooltip__row" key={item.dataKey}>
          <span
            className="owner-chart-tooltip__dot"
            style={{ backgroundColor: item.color }}
          />
          <span className="owner-chart-tooltip__label">{item.name}</span>
          <span className="owner-chart-tooltip__value">
            {formatCurrency(item.value)}
          </span>
        </div>
      ))}
    </div>
  );
};

const PriceTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;

  return (
    <div className="owner-chart-tooltip">
      <p className="owner-chart-tooltip__title">{label}</p>
      {payload
        .filter((item) => item.value != null)
        .map((item) => (
          <div className="owner-chart-tooltip__row" key={item.dataKey}>
            <span
              className="owner-chart-tooltip__dot"
              style={{ backgroundColor: item.color }}
            />
            <span className="owner-chart-tooltip__label">{item.name}</span>
            <span className="owner-chart-tooltip__value">
              {formatCurrency(item.value)}/L
            </span>
          </div>
        ))}
    </div>
  );
};

const OwnerPurchaseInsightsChart = ({
  data = [],
  series = [],
  priceData = [],
  priceSeries = [],
  summary = {},
  bySupplier = [],
  byProduct = [],
  loading = false,
}) => {
  const areaSeries = useMemo(
    () => series.filter((item) => item.type !== "total"),
    [series]
  );
  const totalSeries = useMemo(
    () => series.find((item) => item.type === "total"),
    [series]
  );

  const mixData = useMemo(
    () =>
      (summary?.mix || [])
        .filter((item) => item.amount > 0)
        .map((item, index) => ({
          name: item.category,
          value: item.amount,
          pct: item.pct,
          fill: MIX_COLORS[index % MIX_COLORS.length],
        })),
    [summary]
  );

  if (loading) {
    return (
      <div className="owner-chart-empty">
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">Loading...</span>
        </div>
      </div>
    );
  }

  if (!data.length && !summary?.net_spend) {
    return (
      <div className="owner-chart-empty">
        <i className="fa fa-shopping-cart owner-chart-empty__icon" aria-hidden="true" />
        <p>No purchase data for the selected period.</p>
      </div>
    );
  }

  return (
    <div className="owner-sales-chart">
      <div className="owner-sales-kpi row g-3 mb-4">
        <div className="col-sm-6 col-xl-3">
          <div className="owner-sales-kpi__card owner-sales-kpi__card--brand">
            <span className="owner-sales-kpi__label">Net purchase spend</span>
            <strong className="owner-sales-kpi__value">
              {formatCurrency(summary?.net_spend)}
            </strong>
            {summary?.purchase_returns > 0 ? (
              <small className="owner-sales-kpi__hint">
                Returns: {formatCurrency(summary.purchase_returns)}
              </small>
            ) : null}
          </div>
        </div>
        <div className="col-sm-6 col-xl-3">
          <div className="owner-sales-kpi__card owner-sales-kpi__card--blue">
            <span className="owner-sales-kpi__label">Fuel purchases</span>
            <strong className="owner-sales-kpi__value">
              {formatCurrency(summary?.fuel_spend)}
            </strong>
          </div>
        </div>
        <div className="col-sm-6 col-xl-3">
          <div className="owner-sales-kpi__card owner-sales-kpi__card--amber">
            <span className="owner-sales-kpi__label">Avg fuel buy price</span>
            <strong className="owner-sales-kpi__value">
              {formatCurrency(summary?.avg_fuel_price)}/L
            </strong>
            <small className="owner-sales-kpi__hint">
              {summary?.invoice_count || 0} invoices
            </small>
          </div>
        </div>
        <div className="col-sm-6 col-xl-3">
          <div className="owner-sales-kpi__card owner-sales-kpi__card--green">
            <span className="owner-sales-kpi__label">Top supplier</span>
            <strong className="owner-sales-kpi__value">
              {summary?.top_supplier || "—"}
            </strong>
            <small className="owner-sales-kpi__hint">
              {formatCurrency(summary?.top_supplier_amount)}
            </small>
          </div>
        </div>
      </div>

      <div className="row g-3 mb-4">
        <div className="col-lg-8">
          <div className="owner-chart-panel">
            <p className="owner-chart-panel__subtitle mb-2">Purchase spend trend</p>
            <ResponsiveContainer width="100%" height={360}>
              <ComposedChart data={data} margin={{ top: 12, right: 12, left: 4, bottom: 8 }}>
                <CartesianGrid stroke="#E8ECF1" strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="name"
                  tick={{ fill: "#64748b", fontSize: 12 }}
                  axisLine={{ stroke: "#e2e8f0" }}
                  tickLine={false}
                  dy={8}
                />
                <YAxis
                  tickFormatter={formatCompact}
                  tick={{ fill: "#64748b", fontSize: 12 }}
                  axisLine={false}
                  tickLine={false}
                  width={72}
                />
                <Tooltip content={<SpendTooltip />} />
                <Legend />

                {areaSeries.map((item) => {
                  const color =
                    item.key === "Fuel"
                      ? SPEND_COLORS.fuel
                      : item.key === "Non-Fuel"
                      ? SPEND_COLORS.nonFuel
                      : SPEND_COLORS.cylinder;
                  return (
                    <Area
                      key={item.key}
                      type="monotone"
                      dataKey={item.key}
                      name={item.label}
                      stackId="purchase"
                      stroke={color}
                      fill={color}
                      fillOpacity={0.35}
                      strokeWidth={2}
                      connectNulls
                    />
                  );
                })}

                {totalSeries ? (
                  <Line
                    type="monotone"
                    dataKey={totalSeries.key}
                    name={totalSeries.label}
                    stroke={SPEND_COLORS.total}
                    strokeWidth={2.5}
                    dot={{ r: 3, fill: SPEND_COLORS.total, strokeWidth: 0 }}
                    connectNulls
                  />
                ) : null}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="col-lg-4">
          <div className="owner-chart-panel owner-chart-panel--compact h-100">
            <p className="owner-chart-panel__subtitle mb-2">Purchase mix</p>
            {mixData.length ? (
              <>
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie
                      data={mixData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={82}
                      paddingAngle={2}
                    >
                      {mixData.map((entry) => (
                        <Cell key={entry.name} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value) => formatCurrency(value)}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="owner-expense-pie-legend">
                  {mixData.map((item) => (
                    <div className="owner-expense-pie-legend__row" key={item.name}>
                      <span
                        className="owner-chart-legend__swatch"
                        style={{ backgroundColor: item.fill }}
                      />
                      <span className="owner-expense-pie-legend__label">
                        {item.name}
                      </span>
                      <span className="owner-expense-pie-legend__value">
                        {formatPercent(item.pct)}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="owner-chart-empty owner-chart-empty--compact">
                <p>No purchase mix data.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {priceData.length && priceSeries.length ? (
        <div className="owner-chart-panel mb-4">
          <p className="owner-chart-panel__subtitle mb-2">Avg fuel purchase price</p>
          <p className="small text-muted mb-3">
            Weighted average buy price per litre from approved fuel unloads.
          </p>
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={priceData} margin={{ top: 12, right: 12, left: 4, bottom: 8 }}>
              <CartesianGrid stroke="#E8ECF1" strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="name"
                tick={{ fill: "#64748b", fontSize: 12 }}
                axisLine={{ stroke: "#e2e8f0" }}
                tickLine={false}
                dy={8}
              />
              <YAxis
                tickFormatter={formatCompact}
                tick={{ fill: "#64748b", fontSize: 12 }}
                axisLine={false}
                tickLine={false}
                width={72}
              />
              <Tooltip content={<PriceTooltip />} />
              <Legend />
              {priceSeries.map((item) => {
                const color = resolvePriceColor(item);
                return (
                  <Line
                    key={item.key}
                    type="monotone"
                    dataKey={item.key}
                    name={item.label}
                    stroke={color}
                    strokeWidth={2}
                    dot={{ r: 2, fill: color, strokeWidth: 0 }}
                    connectNulls
                  />
                );
              })}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      ) : null}

      <div className="row g-3">
        <div className="col-lg-6">
          <div className="owner-employee-table-wrap">
            <p className="owner-chart-panel__subtitle mb-2">Top suppliers</p>
            <div className="owner-expense-item-table">
              <div className="owner-expense-item-table__head">
                <span>Supplier</span>
                <span>Amount</span>
                <span>Share</span>
              </div>
              {bySupplier.length ? (
                bySupplier.map((item) => (
                  <div className="owner-expense-item-table__row" key={item.supplier}>
                    <span className="owner-expense-item-table__label">
                      {item.supplier}
                    </span>
                    <span className="owner-expense-item-table__amount">
                      {formatCurrency(item.amount)}
                    </span>
                    <span className="owner-expense-item-table__pct">
                      {formatPercent(item.pct)}
                    </span>
                  </div>
                ))
              ) : (
                <div className="p-3 text-muted small">No supplier data.</div>
              )}
            </div>
          </div>
        </div>

        <div className="col-lg-6">
          <div className="owner-employee-table-wrap">
            <p className="owner-chart-panel__subtitle mb-2">Top purchased products</p>
            <div className="owner-expense-item-table">
              <div className="owner-expense-item-table__head owner-expense-item-table__head--products">
                <span>Product</span>
                <span>Category</span>
                <span>Amount</span>
              </div>
              {byProduct.length ? (
                byProduct.map((item) => (
                  <div
                    className="owner-expense-item-table__row owner-expense-item-table__row--products"
                    key={`${item.category}-${item.product}`}
                  >
                    <span className="owner-expense-item-table__label">
                      {item.product}
                    </span>
                    <span className="owner-expense-item-table__pct">{item.category}</span>
                    <span className="owner-expense-item-table__amount">
                      {formatCurrency(item.amount)}
                    </span>
                  </div>
                ))
              ) : (
                <div className="p-3 text-muted small">No product data.</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OwnerPurchaseInsightsChart;
